import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from '@strava/ui/clsx';

import StravaMediaLightbox from 'components/StravaMediaLightbox';

import { photoListProps } from '../../../../props/feed-prop-types';
import { ADD_AUTOPLAY_VIDEO } from '../../entity-actions';
import { useFeedEntityContext } from '../../FeedEntityContext';
import useFeedAnalytics from '../../../../hooks/useFeedAnalytics';
import useFeedIntersectionObserver from '../../../../hooks/useFeedIntersectionObserver';

import VideoItem from './VideoItem';

import styles from '../styles.scss';

// photo image entries and their associated tags
const PhotoGrid = ({
  activityId,
  photoList,
  maxNumPhotosToShow,
  singleImageHighlighted,
  entryTypeClassName
}) => {
  const {
    feedEntityState,
    analyticProps,
    feedDispatch
  } = useFeedEntityContext();
  const { entityId, page, feedType } = analyticProps;
  const [params, setParams] = useState({
    isOpen: false,
    photoIndex: 0
  });

  const { inView, intersectionObserverRef } = useFeedIntersectionObserver({
    isParentFeedEntry: false
  });

  photoList.forEach((media, index) => {
    const orderIndex = `${index + 1}/${photoList.length}`;
    if (media.media_type === 1) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useFeedAnalytics({
        ...analyticProps,
        element: 'image',
        inView,
        properties: {
          image_id: media.photo_id,
          has_description: media.caption_escaped.length > 0,
          order: orderIndex,
          activity_athlete_id: media.owner_id
        }
      });
    } else {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useFeedAnalytics({
        ...analyticProps,
        element: 'video',
        inView,
        properties: {
          video_id: media.photo_id,
          has_description: media.caption_escaped.length > 0,
          order: orderIndex,
          activity_athlete_id: media.owner_id
        }
      });
    }
  });

  const photoCount = photoList.length;
  const showPhotoLightbox = params.isOpen && photoCount > 0;

  const firstVideoId = useMemo(() => {
    // Skip item that's under the "+3" element aka more_images element as
    // we do not want to autoplay it when there are more then max number of
    // elements
    const maxNumVideoCanAutoplay =
      maxNumPhotosToShow < photoList.length
        ? maxNumPhotosToShow - 1
        : maxNumPhotosToShow;

    const filtered = photoList
      .slice(0, maxNumVideoCanAutoplay)
      .filter((p) => p?.media_type === 2)
      .map((p) => p.photo_id);

    return filtered.length > 0 ? filtered[0] : null;
  }, [photoList, maxNumPhotosToShow]);

  const snowplowMediaClickEvent = (index) => {
    const media = photoList[index];
    const isImage = media.media_type === 1;
    const orderIndex = `${index + 1}/${photoList.length}`;
    const properties = {
      has_description: media.caption_escaped.length > 0,
      order: orderIndex,
      activity_athlete_id: media.owner_id
    };
    if (isImage) {
      properties.image_id = media.photo_id;
    } else {
      properties.video_id = media.photo_id;
      properties.has_description = media.caption_escaped.length > 0;
    }
    feedEntityState.trackAnalytics({
      action: 'click',
      element: media.media_type === 1 ? 'image' : 'video',
      properties
    });
  };

  const openLightBox = (index) => {
    snowplowMediaClickEvent(index);
    setParams({ isOpen: true, photoIndex: index });
  };

  const onMediaClick = (media) => {
    const data = {
      category: feedType,
      page,
      action: 'click',
      properties: {
        source: feedType,
        entity_id: entityId,
        entity_type: page,
        element_entity_type: media.media_type,
        element_entity_id: media.photo_id
      }
    };

    const isImage = media.media_type === 1;
    feedEntityState.trackAnalytics({
      ...data,
      action: 'click',
      element: isImage ? 'photo' : 'video'
    });
  };

  const photoGrid = () => {
    const photoGridElements = [];
    if (singleImageHighlighted) {
      // Single image grid
      // Only the first photo (highlighted image) is displayed on posts
      // Image on posts do not open the lightbox
      const highlightedImageForPosts = photoList[0];
      photoGridElements.push(
        <div
          className={cx(entryTypeClassName, styles.entryImage)}
          data-testid="post"
          key="single_image_grid"
          ref={intersectionObserverRef}
        >
          <div className={styles.entryImageWrapper}>
            <img
              src={highlightedImageForPosts.large}
              srcSet={highlightedImageForPosts.large}
              alt={highlightedImageForPosts.caption_escaped}
            />
          </div>
        </div>
      );
    } else {
      // Multiple photos grid
      photoGridElements.push(
        photoList.slice(0, maxNumPhotosToShow).map((photo, index) => {
          const { photo_id: photoId, large, caption_escaped: caption } = photo;

          if (photo?.media_type === 2) {
            return (
              <div
                data-testid="video"
                key={photoId}
                className={styles.entryImage}
                ref={intersectionObserverRef}
                onTouchStart={() => {
                  openLightBox(index);
                }}
              >
                <VideoItem
                  className={cx(
                    styles.entryImageWrapper,
                    styles.entryGridImage
                  )}
                  media={photo}
                  isAutoPlayItem={
                    feedEntityState.autoPlayVideoInView &&
                    photoId === firstVideoId
                  }
                  playOnHover={index !== maxNumPhotosToShow - 1}
                  onClick={() => {
                    openLightBox(index);
                  }}
                  onReady={(video) => {
                    if (photoId === firstVideoId) {
                      feedDispatch({
                        type: ADD_AUTOPLAY_VIDEO,
                        payload: {
                          autoPlayVideo: {
                            [activityId]: video
                          }
                        }
                      });
                    }
                  }}
                />
              </div>
            );
          }
          // Photos
          return (
            <div
              data-testid="photo"
              key={photoId}
              className={styles.entryImage}
              ref={intersectionObserverRef}
            >
              <div
                className={cx(styles.entryImageWrapper, styles.entryGridImage)}
                onClick={() => openLightBox(index)}
                onKeyPress={() => openLightBox(index)}
                role="button"
                tabIndex="0"
              >
                {/* Enhanced Partner Tag */}
                {photo.showEnhancedTag && photo.enhanced_photo && (
                  <div data-testid="partner_tag" className={styles.enhancedTag}>
                    {photo.enhanced_photo.name}
                  </div>
                )}
                <img src={large} alt={caption} />
              </div>
            </div>
          );
        })
      );
      // show count for more images eg. +5 indicating that there
      // are 5 more images uploaded by the athlete that are not being currently shown on the
      // photo grid.
      if (photoCount > maxNumPhotosToShow) {
        const extraPhotos = photoCount - maxNumPhotosToShow;
        photoGridElements.push(
          <div
            key="more_images"
            className={styles.entryMoreImages}
            onClick={() => snowplowMediaClickEvent(maxNumPhotosToShow)}
            onKeyPress={() => snowplowMediaClickEvent(maxNumPhotosToShow)}
            role="button"
            tabIndex="0"
            ref={intersectionObserverRef}
          >
            <div className={styles.moreText}>
              <div data-testid="more-text">{`+ ${extraPhotos}`}</div>
            </div>
          </div>
        );
      }
    }
    // Add photo lightbox
    if (showPhotoLightbox) {
      photoGridElements.push(
        <StravaMediaLightbox
          key="photo_lightbox"
          selectedPhotoIndex={params.photoIndex}
          photoList={photoList}
          onMount={() => {
            onMediaClick(photoList[params.photoIndex]);
          }}
          onCloseRequest={() => {
            setParams({ isOpen: false });
          }}
          analyticProps={analyticProps}
        />
      );
    }

    return photoGridElements;
  };

  return photoGrid();
};

PhotoGrid.propTypes = {
  activityId: PropTypes.string,
  photoList: photoListProps.isRequired,
  maxNumPhotosToShow: PropTypes.number,
  singleImageHighlighted: PropTypes.bool,
  entryTypeClassName: PropTypes.oneOf([
    styles.activityMap,
    styles.groupActivityMap,
    styles.postImage
  ]).isRequired
};

PhotoGrid.defaultProps = {
  activityId: null,
  maxNumPhotosToShow: 1,
  singleImageHighlighted: false
};

export default PhotoGrid;
